import { useEffect, useState, useRef } from "react";
import TopBar from "../../layouts/postLogin/TopBar";
import { TabsData } from "../../utils/TabsData";
import Tabs from '../sampleSize/Tabs';
import { useLocation, useNavigate } from "react-router-dom";
import { fetchRequestId, fetchUserCredits, getArticles } from "./apiController";
import { useDispatch, useSelector } from "react-redux";
import { ChatContext, chatSelector, clearMessages, Message, setContextData, setCurrentContext, setIsOpen, setReInit, setResult } from "../../store/slices/chatSlice";
import { sampleArticles } from "./sampleArticles";
import ViewArticle from "./ViewArticle";
import ModalComponent from "../common/Modal/modal";
import { Col, Row } from "reactstrap";
import { FieldItem } from "../../widgets/fields";
import { FormElements } from "./FormElemets";
import { CustomButton } from "../../widgets";
import useFieldItem from "../../widgets/fields/UseFieldItem";
import { sortBy } from "lodash";
import { setLoading } from "../../store/slices/auth";
import NoUserCreditsModal from "../common/AiModuleComponents/NoUserCreditsModal";
// import AiAgentChat, { Message } from "../common/AiChatComponent/AiAgentChat";

interface Article {
  id: string | number;
  title: string;
  abstract: string;
  authors: string[];
  doi: string;
  source: string;
  relevanceScore?: number;
}

interface ProjectData {
  TopicDesc: string;
  key_words: string[];
  id?: string; // Added for topic_id
  [key: string]: any; // For any other properties in project data
}

const LiteratureSearch = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [projectData, setProjectData] = useState<ProjectData | null>(null);
  const [articlesData, setArticlesData] = useState<any[] | null>(null);
  const dispatch = useDispatch();
  const [isRelevanceCheckActive, setIsRelevanceCheckActive] = useState<boolean>(false);
  const [action, setAction] = useState<string>("");
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);
  const [showFilterModal, setShowFilterModal] = useState<boolean>(false);
  const [showQueryModal, setShowQueryModal] = useState<boolean>(false);
  const [selectedQuery, setSelectedQuery] = useState<any>("");
  const [queryData, setQueryData] = useState<any[]>([]);
  const [selectedQueryArticlesData, setSelectedQueryArticlesData] = useState<any>([]);
  const [hasInitialArticles, setHasInitialArticles] = useState<boolean>(false);
  const [isCreditsAvailable, setIsCreditsAvailable] = useState<boolean>(true);
  const [filters, setFilters] = useState<any>({
    source: { label: "All", value: "all" },
    author: '',
    title: '',
    sortBy: { label: "Most Relevant", value: "mostRelevant" },
  });
  const [visitedArticles, setVisitedArticles] = useState<Set<string>>(new Set());

  const { isLoading, result, chattingInProgress, isConnected, reInit } = useSelector(chatSelector);

  useEffect(() => {
    const storedVisited = localStorage.getItem("visitedArticles");
    if (storedVisited) {
      try {
        setVisitedArticles(new Set(JSON.parse(storedVisited)));
      } catch (e) {
        console.error("Failed to parse visitedArticles from localStorage", e);
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("visitedArticles", JSON.stringify(Array.from(visitedArticles)));
  }, [visitedArticles]);

  const getSortedAndFilteredArticles = (rawArticles: any[]) => {
    if (!rawArticles) return [];
    let filtered = [...rawArticles];
    let source = filters.source?.value || filters.source;
    let author = filters.author;
    let title = filters.title;
    let sortByValue = filters.sortBy?.value || filters.sortBy;

    // Filter by source
    if (source && source !== "all") {
      filtered = filtered.filter(article => article.source.toLowerCase() === source.toLowerCase());
    }

    // Search by author
    if (author) {
      filtered = filtered.filter(article =>
        article.authors.some(authorName =>
          authorName.toLowerCase().includes(author.toLowerCase())
        )
      );
    }

    // Search by title
    if (title) {
      filtered = filtered.filter(article =>
        article.title.toLowerCase().includes(title.toLowerCase())
      );
    }

    // Sort by most recent
    if (sortByValue === 'mostRecent') {
      filtered.sort((a, b) => parseInt(b.year) - parseInt(a.year));
    }

    // Sort by least recent
    if (sortByValue === 'leastRecent') {
      filtered.sort((a, b) => parseInt(a.year) - parseInt(b.year));
    }

    // Sort by most relevant
    if (sortByValue === 'mostRelevant') {
      filtered.sort((a, b) =>
        (b.relevance_check?.relevance_score || 0) - (a.relevance_check?.relevance_score || 0)
      );
    }

    // Sort by least relevant
    if (sortByValue === 'leastRelevant') {
      filtered.sort((a, b) =>
        (a.relevance_check?.relevance_score || 0) - (b.relevance_check?.relevance_score || 0)
      );
    }

    return filtered;
  };

  useEffect(() => {
    if (articlesData && selectedQuery?.value) {
      const selectedArticles = articlesData?.find((item) => item.query === selectedQuery.value)?.articles;
      if (selectedArticles) {
        const sortedAndFiltered = getSortedAndFilteredArticles(selectedArticles);
        setSelectedQueryArticlesData(sortedAndFiltered);
      }
    }
  }, [articlesData, selectedQuery, filters]);
  useEffect(() => {
    if (location.state) {
      let tempState = location.state as ProjectData;
      setProjectData(tempState);
      localStorage.setItem("projectData", JSON.stringify(tempState));
      getArticlesAndInitializeSearch(tempState);
    } else {
      navigate("/project");
    }

  }, [location.state, navigate, dispatch]);

  useEffect(() => {
    if (projectData && result && result.resultStatus && result.resultStatus.length > 0) {
      fetchArticles(projectData);
      // if (result.module === ChatContext.LITERATURE_SEARCH && result.resultStatus === "success") {
      //   initiateRelevanceCheck();
      // }
    }
  }, [result]);

  async function checkUserCredits(tempState: any = null) {
    try {
      dispatch(setLoading(true));
      const aiAgents: any = JSON.parse(localStorage.getItem("aiAgents") || "[]");
      const literatureSearchAgent = aiAgents?.find((agent) => agent.name === "Literature Search");
      const response: any = await fetchUserCredits(literatureSearchAgent?.id, tempState?.TopicID || projectData?.TopicID);
      if (response?.status) {
        setIsCreditsAvailable(true);
        return true;
      } else {
        setIsCreditsAvailable(false);
        return false;
      }
    } catch (error) {
      console.error(error);
    } finally {
      dispatch(setLoading(false));
    }
  }

  async function fetchArticles(projectData) {
    dispatch(setLoading(true))
    try {
      const response: any = await getArticles(projectData?.TopicID);
      if (response.data) {
        setArticlesData(response?.data);
        getQueryData(response?.data);
      }
    } catch (error) {
      console.log(error)
    }
    finally {
      dispatch(setLoading(false))
    }
  }

  async function getArticlesAndInitializeSearch(projectData: ProjectData) {
    dispatch(setLoading(true))
    try {
      const response: any = await getArticles(projectData?.TopicID);
      if (response.data) {
        setArticlesData(response?.data);
        getQueryData(response?.data);
      } else {
        // initiateLiteratureSearch();
        setSelectedQueryArticlesData([])
      }
    } catch (error) {

      console.log(error)
    } finally {
      dispatch(setLoading(false))
    }
  }

  const initiateLiteratureSearch = async () => {
    const userCreditsAvailable = await checkUserCredits();
    if (!userCreditsAvailable) {
      return;
    }
    if (!projectData) {
      console.error("initiateLiteratureSearch called without projectData");
      return;
    }
    const aiAgents: any = JSON.parse(localStorage.getItem("aiAgents") || "[]");
    const literatureSearchAgent = aiAgents?.find((agent) => agent.name === "Literature Search");

    const requestIdResponse: any = await fetchRequestId(projectData?.TopicID, literatureSearchAgent?.id);

    const projectDataLocal = { ...projectData, request_id: requestIdResponse?.request_id };
    dispatch(clearMessages({ module: ChatContext.LITERATURE_SEARCH }));
    dispatch(setCurrentContext(ChatContext.LITERATURE_SEARCH));
    dispatch(setContextData(projectDataLocal));
    dispatch(setIsOpen(true));
    dispatch(setResult({
      module: ChatContext.LITERATURE_SEARCH,
      resultStatus: "",
    }));
    dispatch(setReInit(!reInit));
  }

  const getQueryData = (articlesdata) => {
    const validArticles = articlesdata.filter((article) => article.query && article.articles && article.articles.length > 0);
    const querydata = validArticles.map((article) => ({
      label: article.query,
      value: article.query
    }));
    setQueryData(querydata);
    if (querydata.length > 0) {
      const lastQuery = querydata[querydata.length - 1];
      setSelectedQuery(lastQuery);
      const initialArticles = validArticles.find((item) => item.query === lastQuery.value)?.articles;
      setSelectedQueryArticlesData(getSortedAndFilteredArticles(initialArticles));
    }

    // Set flag to indicate we have loaded initial articles
    setHasInitialArticles(true);
  }



  const initiateRelevanceCheck = () => {
    // Check for required data
    if (!projectData?.TopicID || !selectedQuery?.value) {
      console.error("Missing required data for relevance check", {
        topicId: projectData?.TopicID,
        query: selectedQuery?.value,
      });
      return;
    }

    dispatch(clearMessages({ module: ChatContext.RELEVANCE_CHECK }));
    dispatch(setCurrentContext(ChatContext.RELEVANCE_CHECK));
    dispatch(setContextData({
      topicId: projectData.TopicID,
      query: selectedQuery.value,
    }));
    dispatch(setIsOpen(true));
    dispatch(setResult({
      module: ChatContext.RELEVANCE_CHECK,
      resultStatus: "",
    }));
    dispatch(setReInit(!reInit));
  };

  useEffect(() => {
    if (selectedArticle && articlesData) {
      const selectedArticles = articlesData?.find((item) => item.query === selectedQuery.value)?.articles;
      if (selectedArticles) {
        const updatedArticle = selectedArticles.find(a => a.doi === selectedArticle.doi);
        if (updatedArticle) {
          setSelectedArticle(updatedArticle);
        }
      }
    }
  }, [articlesData, selectedQuery]);

  const onAction = (article: Article, action: string) => {
    if (action === "viewArticle") {
      setSelectedArticle(article);
      setAction("viewArticle");
      if (article.doi) {
        setVisitedArticles(prev => new Set(prev).add(article.doi));
      }
    }
  }

  const applyFiltersToArticles = () => {
    setShowFilterModal(false);
  }


  const handleQueryChange = (query: any) => {
    const selectedArticles = articlesData?.find((item) => item.query === query)?.articles;
    setSelectedQueryArticlesData(getSortedAndFilteredArticles(selectedArticles));
    setSelectedQuery({ label: query, value: query });
    setShowQueryModal(false);
  };

  const handleFilterChange = (e: any, name) => {
    if (e[1]?.isTouched) {
      return;
    }
    let value = e[0];
    if (name === "source") {
      value = e[0].value
    }
    if (name === "sortBy") {
      value = e[0].value
    }
    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value,
    }));
  };

  const clearFilters = () => {
    setFilters({
      source: { label: "All", value: "all" },
      author: '',
      title: '',
      sortBy: { label: "None", value: "none" },
    });
  }


  return (
    <>
      {showFilterModal &&
        <ModalComponent
          width={window.innerWidth > 700 ? window.innerWidth / 2 : window.innerWidth}
          isOpen={true}
          onClose={() => setShowFilterModal(false)}
          header={"Filters"}
          centered={false}
          customClass="literature-search-filters"
          body={
            <>
              <Row>
                <Col md={6}>
                  <FieldItem
                    {...FormElements.titleSearch}
                    value={filters.title}
                    onChange={(...e) => {
                      handleFilterChange(e, "title")
                    }}
                    isDisabled={isLoading}
                  />
                </Col>
                <Col md={6}>
                  <FieldItem
                    {...FormElements.authorSearch}
                    value={filters.author}
                    onChange={(...e) => {
                      handleFilterChange(e, "author")
                    }}
                    isDisabled={isLoading}
                  />
                </Col>
                <Col md={6}>
                  <FieldItem
                    {...FormElements.sourceDropdown}
                    value={filters.source}
                    onChange={(...e: any) => {
                      setFilters((prevFilters) => ({
                        ...prevFilters,
                        source: e[0],
                      }));
                    }}
                    isDisabled={isLoading}
                  />
                </Col>
                <Col md={6}>
                  <FieldItem
                    {...FormElements.sortByDropdown}
                    value={filters.sortBy}
                    onChange={(...e: any) => {
                      setFilters((prevFilters) => ({
                        ...prevFilters,
                        sortBy: e[0],
                      }))
                    }}
                    isDisabled={isLoading}
                  />
                </Col>
              </Row>

            </>
          }
          footer={
            <>
              <CustomButton isDisabled={isLoading} text="Clear" type="primary-alert" onClick={() => clearFilters()}></CustomButton>
              <CustomButton isDisabled={isLoading} text="Apply" type="primary" onClick={() => applyFiltersToArticles()}></CustomButton>
            </>
          }
        />
      }
      {showQueryModal &&
        <ModalComponent
          width={window.innerWidth > 700 ? window.innerWidth / 2 : window.innerWidth}
          isOpen={true}
          onClose={() => setShowQueryModal(false)}
          header={"Queries"}
          centered={false}
          customClass="literature-search-query-modal"
          body={
            <>
              <Row>
                <Col md={12}>
                  <FieldItem
                    {...FormElements.queryRadioButtons}
                    value={selectedQuery.value}
                    values={queryData}
                    onChange={(...e) => {
                      handleQueryChange(e[0]);
                    }}
                    isDisabled={isLoading}
                  />
                </Col>
              </Row>
            </>
          }
          footer={<></>}
        />
      }

      {
        action === "viewArticle" &&
        <ViewArticle
          article={selectedArticle}
          onClose={() => setAction("")}
          topicId={projectData?.TopicID}
          query={selectedQuery?.value}
          onFeedbackUpdate={() => fetchArticles(projectData)}
        />
      }

      {
        isCreditsAvailable === false &&
        <NoUserCreditsModal />
      }

      {

        action === "" &&
        <>
          <TopBar
            projectData={projectData}
            navigate={window.innerWidth > 750 ? "/project" : "/projectmobile"}
          />
          <Tabs
            data={TabsData}
            selectedTab={2}
            projectData={projectData}
          />

          {selectedQueryArticlesData && selectedQueryArticlesData.length > 0 && <div className="literature-search-page">
            <div className="literature-search-header">
              <div className="results-count">
                <span className="count-badge">{selectedQueryArticlesData.length} Results</span>
              </div>
              <div className="query-button">
                <CustomButton
                  text="Query"
                  type="primary"
                  onClick={() => setShowQueryModal(true)}
                  isDisabled={isLoading || isConnected}
                />
              </div>
              <div className="relevance-check-button">
                <CustomButton
                  text="Reinitiate Literature Search"
                  type="primary"
                  onClick={() => initiateLiteratureSearch()}
                  isDisabled={isLoading || isConnected}
                />
              </div>
              {/* <div className="relevance-check-button">
                <CustomButton
                  text="Initiate Relevance Check"
                  type="primary"
                  onClick={() => initiateRelevanceCheck()}
                  isDisabled={isLoading || isConnected}
                />
              </div> */}
              <div className="filters-button">
                <CustomButton
                  text="Filters"
                  type="primary"
                  onClick={() => setShowFilterModal(true)}
                  isDisabled={isLoading || isConnected}
                />
              </div>

            </div>
            <div className={`literature-content`}>
              <div className="literature-search-grid">
                {selectedQueryArticlesData ? (selectedQueryArticlesData?.map((article, indx) => (
                  article.title && article.title.length > 0 &&
                  <div key={article.doi || "" + indx} className={`literature-search-card ${article.doi && visitedArticles.has(article.doi) ? 'visited' : ''}`}>
                    <div className="article-content-area">
                      <h3 className="article-title">{article.title}</h3>
                      <div className="article-abstract">{article.abstract}</div>
                    </div>
                    <div className="literature-search-card-bottom">
                      <div className="authors">
                        Authors: {article.authors.map((author, index) => (
                          <span key={`${index}+${author}+${article.doi}`} className="author-name">
                            {author}{index !== article.authors.length - 1 && ", "}
                          </span>
                        ))}
                      </div>
                      <div
                        className={`relevance-check-text ${isRelevanceCheckActive ? 'disabled' : ''}`}
                        onClick={() => onAction(article, "viewArticle")}
                        style={{ opacity: isRelevanceCheckActive ? 0.5 : 1, cursor: isRelevanceCheckActive ? 'not-allowed' : 'pointer' }}
                      >
                        View
                      </div>
                    </div>
                  </div>
                ))
                ) : (
                  <div className="loading-text">
                    {isLoading ? "Loading articles..." : "No articles found"}
                  </div>
                )}
              </div>
            </div>
          </div>}

          {hasInitialArticles && (!selectedQueryArticlesData || selectedQueryArticlesData.length === 0) && (
            <div className="literature-search-page">
              <div className="literature-search-header">
                <div className="query-button">
                  <CustomButton
                    text="Query"
                    type="primary"
                    onClick={() => setShowQueryModal(true)}
                    isDisabled={isLoading || isConnected}
                  />
                </div>
                <div className="relevance-check-button">
                  <CustomButton
                    text="Reinitiate Literature Search"
                    type="primary"
                    onClick={() => initiateLiteratureSearch()}
                    isDisabled={isLoading || isConnected}
                  />
                </div>
                <div className="filters-button">
                  <CustomButton
                    text="Filters"
                    type="primary"
                    onClick={() => setShowFilterModal(true)}
                    isDisabled={isLoading || isConnected}
                  />
                </div>
              </div>
              <div className="literature-content">
                <div className="literature-search-grid">
                  <div className="loading-text">
                    No articles found matching the current filters. Try changing the query.
                  </div>
                </div>
              </div>
            </div>
          )}

          {!hasInitialArticles && (!selectedQueryArticlesData || selectedQueryArticlesData.length === 0) && (
            <div className="literature-search-button-container">
              <CustomButton
                text="Initiate Literature Search"
                type="primary"
                onClick={() => initiateLiteratureSearch()}
                isDisabled={isLoading || isConnected || !isCreditsAvailable}
              />
            </div>
          )}
        </>
      }


    </>
  );
};

export default LiteratureSearch;